import { planApis } from "@payping/api";
import { CheckOutlined } from "@payping/icons";

import { Trans } from "@payping/utils/localeUtils";
import { Badge, Divider, Page, Space, Tag, Typography, useMessagedAsync } from "@payping/web-components";
import classNames from "classnames";
import { FC, useEffect } from "react";
import { useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "../../../assets/scss/profile/Upgrade.module.scss";
import usePlans from "./usePlans";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

const Upgrade: FC = () => {
  const { t } = useAppTranslation();
  const currentPlanId = useAppSelector((state) => state.user.panelInfo?.currentPlan?.id);

  const { value: plansResponse } = useMessagedAsync(
    () => planApis.getAllEnabledProfilePlansWithCancellationTokenGet(),
    [],
  );
  const planList = usePlans(plansResponse, [currentPlanId]);
  const hasDiscount = planList.findIndex((plan) => plan.discountPercent) !== -1;
  const maxLength = Math.max(...planList.map((plan) => plan.features.length));

  useEffect(() => {
    /** function to set equal max height for all parallel spec sections of different plans
     * by finding the height of longest section and applying it to all sections
     * for example: "امکانات کلی" should have the same height for all plans */
    function equalizeSpecHeight() {
      if (window.innerWidth >= 992) {
        for (let i = 0; i < maxLength; i += 1) {
          const specLists = document.getElementsByClassName(`plan-spec-list-${i}`);
          let maxHeight = 0;
          for (let j = 0; j < specLists.length; j += 1) {
            maxHeight = Math.max(maxHeight, specLists[j].children[0].scrollHeight);
          }
          for (let j = 0; j < specLists.length; j += 1) {
            specLists[j].setAttribute("style", `height: ${maxHeight}px`);
          }
        }
      }
    }

    equalizeSpecHeight();
    window.addEventListener("resize", equalizeSpecHeight);
    return () => window.removeEventListener("resize", equalizeSpecHeight);
  }, [maxLength]);

  return (
    <Page>
      <div className={classNames(styles.plansRow, "tw:mt-8!")}>
        {planList.map((plan, i) => (
          <div
            key={i}
            className={classNames(styles.planContainer, {
              [styles.recommendedPlan]: plan.isRecommendedPlan,
              [styles.premiumPlan]: plan.premium,
            })}
          >
            {plan.isRecommendedPlan ? (
              <div className={styles.recommendedPlanSection}>
                <span className={styles.recommendedPlanCta}>{t("upgrade.plans.paypingRecommendation")}</span>
              </div>
            ) : null}
            <div className={styles.planDetails}>
              <div>
                <Typography.Text className="font-em-20">{plan.title}</Typography.Text>
                {plan.isRecommendedPlan || plan.trialDays ? (
                  <div className={classNames(styles.recommendedPlanRow, "color--gray500")}>
                    {plan.isRecommendedPlan ? (
                      <span className={styles.recommendedPlanCta}>{t("upgrade.plans.paypingRecommendation")}</span>
                    ) : null}
                    {plan.trialDays ? <span>{t("upgrade.freeCommission", { days: plan.trialDays })}</span> : null}
                  </div>
                ) : null}
              </div>

              <div
                className="tw:flex-col"
                style={plan.discountPercent ? { height: 100 } : { height: 70, marginTop: hasDiscount ? 30 : "" }}
              >
                <Typography.Text className="font-em-20" style={{ color: "inherit" }}>
                  {plan.discountPercent ? (
                    <>
                      <div style={{ fontSize: "0.6em" }}>
                        <span className="tw:line-through">{plan.priceText}</span>{" "}
                        <span className="color--danger" style={{ fontSize: "0.6em" }}>
                          {formatLocalizedNumber({
                            value: plan.discountPercent,
                            options: { maximumFractionDigits: 2 },
                          })}
                          % {t("common.discount")}
                        </span>
                      </div>
                      <span>
                        {plan.discountedPrice
                          ? formatLocalizedNumber({
                              value: plan.discountedPrice,
                              options: { maximumFractionDigits: 2 },
                            })
                          : t("upgrade.free")}
                      </span>
                    </>
                  ) : (
                    plan.priceText
                  )}
                </Typography.Text>
                <Typography.Text>{plan.priceDescription}</Typography.Text>
              </div>
              {!plan.premium ? (
                <div className={classNames(styles.planDetailsSection, "tw:flex tw:justify-center")}>
                  <span className="color--gray500">{plan.wage}</span>
                </div>
              ) : null}

              <div className={classNames(styles.planDetailsSection, plan.premium ? "tw:h-[200px]" : "")}>
                <span className={classNames({ "color--gray500": !plan.premium }, "font-n4")}>{plan.description}</span>
              </div>

              <div className={classNames(styles.planDetailsSection, "tw:flex tw:h-[110px] tw:items-center")}>
                {plan.refund}
                {plan.button}
                {plan.buttonSub}
              </div>

              <div className={styles.planSpecifications} id={`plan-specifications-${plan.id}`}>
                {plan.features.map((value, index) => (
                  <>
                    <div className={`plan-spec-list-${index}`}>
                      <div>
                        <Divider className={"border--gray100"} />
                        {value !== "fakeFeature" && (
                          <>
                            <Typography.Paragraph>
                              <Trans
                                i18nKey={`User:upgrade.plans.featuresNames.${value}`}
                                components={{ Badge: <Badge status={"error"} />, div: <div />, span: <span /> }}
                              />
                            </Typography.Paragraph>
                            <ul className={styles.featureList}>
                              {(
                                t(`upgrade.plans.${plan.translationKey}.features.${value}`, {
                                  returnObjects: true,
                                }) as any
                              )?.map((feat: string, j: number) => (
                                <li key={j} className={"tw:flex-row"}>
                                  <Typography.Text>
                                    <Space align={"start"} orientation={"horizontal"}>
                                      <span className={classNames(styles.icon)}>
                                        <CheckOutlined width={"0.8em"} height={"0.8em"} fill={"#52c41a"} />
                                      </span>
                                      <Trans
                                        components={{
                                          Tag: <Tag className={"inline"} color={"error"} />,
                                          b: <b />,
                                          a: (
                                            <a
                                              href="https://payping.io/landing/tax-advice/"
                                              target="_blank"
                                              className="tw:no-underline"
                                              rel="noreferrer"
                                            />
                                          ),
                                          span: <span />,
                                        }}
                                      >
                                        {feat}
                                      </Trans>
                                    </Space>
                                  </Typography.Text>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                ))}
              </div>
            </div>
            {plan.premium ? (
              <div className={styles.premiumPlanBottomSpace}></div>
            ) : (
              <Space orientation={"vertical"} className={styles.planDetailsFooter}>
                <div className={"tw:flex-col"}>
                  <Typography.Text className="font-em-15">
                    {plan.discountPercent
                      ? plan.discountedPrice
                        ? formatLocalizedNumber({ value: plan.discountedPrice })
                        : t("upgrade.free")
                      : plan.priceText}
                  </Typography.Text>
                  <Typography.Text>{plan.priceDescription}</Typography.Text>
                </div>
                {plan.button}
              </Space>
            )}
          </div>
        ))}
      </div>
    </Page>
  );
};

export default Upgrade;
